/**
 * @fileoverview Revenue & Ad Spend over time area chart for a single product.
 * @module features/home/components/product-statistics/RevenueOverTimeChart
 */

import { type ReactElement, useMemo } from 'react'
import { Bar, CartesianGrid, ComposedChart, LabelList, ReferenceLine, XAxis, YAxis } from 'recharts'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { formatIsoWeekRange, isIsoWeekPeriod } from '@/shared/utils/dateUtils'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { adaptiveMaxBarSize, computeLabeledBarIndices } from '@/shared/utils/chartBars'
import type { TimeSeriesPoint } from '@/features/home/hooks/useProductStatistics'

interface RevenueOverTimeChartProps {
  data: TimeSeriesPoint[]
  className?: string
}

interface ChartPoint {
  period: string
  label: string
  revenue: number
  adSpend: number
}


function formatPeriodLabel(period: string): string {
  try {
    if (period.length === 7) return format(parseISO(`${period}-01`), 'MMM yy')
    return format(parseISO(period), 'MMM d')
  } catch {
    return period
  }
}

export function RevenueOverTimeChart({
  data,
  className,
}: RevenueOverTimeChartProps): ReactElement {
  const { t } = useTranslation('features-home')
  const compactMoney = useCompactMoney()

  const chartConfig: ChartConfig = useMemo(() => ({
    revenue: { label: t('productStatistics.revenueOverTime.revenue'), color: 'var(--chart-legend-revenue)' },
    adSpend: { label: t('productStatistics.revenueOverTime.adSpend'), color: 'var(--chart-legend-adspend)' },
  }), [t])

  const chartData = useMemo<ChartPoint[]>(
    () => data.map((p) => ({
      period: p.period,
      label: formatPeriodLabel(p.period),
      revenue: p.sales,
      adSpend: p.advertising,
    })),
    [data]
  )

  const tickInterval = useMemo(() => {
    const len = chartData.length
    if (len > 20) return Math.ceil(len / 8)
    if (len > 10) return Math.ceil(len / 6)
    return Math.ceil(len / 4)
  }, [chartData.length])

  const totals = useMemo(() => {
    let revenue = 0
    let adSpend = 0
    for (const d of chartData) {
      revenue += d.revenue
      adSpend += d.adSpend
    }
    return { revenue, adSpend }
  }, [chartData])

  const hasData = chartData.length > 0

  const maxBarSize = adaptiveMaxBarSize(chartData.length)

  // Every revenue bar gets a value label: daily revenue is volatile enough
  // that neighboring labels sit at different heights and rarely collide.
  // The one real collision case — immediate neighbors at nearly the same
  // height — is guarded: the shorter bar's label is skipped.
  const labeledIndices = useMemo(
    () => computeLabeledBarIndices(chartData.map((d) => d.revenue)),
    [chartData]
  )

  function renderBarLabel(props: unknown): ReactElement | null {
    const { x, y, width, value, index } = props as {
      x?: number
      y?: number
      width?: number
      value?: number
      index?: number
    }
    if (index === undefined || !labeledIndices.has(index)) return null
    if (typeof value !== 'number' || value <= 0) return null
    if (x === undefined || y === undefined || width === undefined) return null
    return (
      <text
        x={x + width / 2}
        y={y - 8}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill="var(--chart-legend-revenue)"
      >
        {compactMoney(Math.round(value))}
      </text>
    )
  }

  return (
    <div className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('productStatistics.revenueOverTime.title')}</h3>
        {hasData && (
          <div className="flex items-center gap-2 text-xs sm:gap-4 sm:text-sm">
            <div className="flex items-center gap-1.5">
              <div
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: 'var(--chart-legend-revenue)' }}
              />
              <span className="whitespace-nowrap text-muted-foreground">{t('productStatistics.revenueOverTime.revenue')}</span>
              <span className="whitespace-nowrap font-mono font-semibold text-foreground">
                {compactMoney(totals.revenue)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: 'var(--chart-legend-adspend)' }}
              />
              <span className="whitespace-nowrap text-muted-foreground">{t('productStatistics.revenueOverTime.adSpend')}</span>
              <span className="whitespace-nowrap font-mono font-semibold text-foreground">
                {compactMoney(totals.adSpend)}
              </span>
            </div>
          </div>
        )}
      </div>

      {!hasData ? (
        <p className="text-sm text-muted-foreground/60 text-center py-8">{t('productStatistics.revenueOverTime.noData')}</p>
      ) : (
        <div className="pt-6">
          <ChartContainer config={chartConfig} className="h-chart-lg w-full">
            {/* Top margin reserves room for the value label above the tallest
                bar, which reaches dataMax (the top of the plot area). */}
            <ComposedChart data={chartData} margin={{ top: 32, right: 12, bottom: 20, left: 12 }}>
              <CartesianGrid
                vertical={false}
                horizontal
                stroke="var(--color-muted-foreground)"
                strokeOpacity={0.07}
                strokeDasharray="8 4"
                shapeRendering="crispEdges"
              />
              <ReferenceLine
                y={0}
                stroke="var(--color-muted-foreground)"
                strokeOpacity={0.15}
                strokeWidth={1}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={20}
                tick={{ fontSize: 11, dy: 10 }}
                interval={tickInterval}
              />
              {/* Hidden duplicate axis so the ad-spend bars overlay the
                  revenue bars (same slot) instead of sitting beside them. */}
              <XAxis dataKey="label" xAxisId="overlay" hide />
              <YAxis
                orientation="right"
                tickLine={false}
                axisLine={false}
                tickMargin={13}
                tick={{ fontSize: 12, textAnchor: 'start' }}
                tickFormatter={(v: number) => compactMoney(v)}
              />
              <ChartTooltip
                cursor={false}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  // Weekly buckets show the full Mon–Sun range; the X-axis keeps the compact label.
                  const point = payload[0]?.payload as ChartPoint | undefined
                  const title =
                    point && isIsoWeekPeriod(point.period)
                      ? formatIsoWeekRange(point.period)
                      : (point?.label ?? '')
                  return (
                    <div className="rounded-lg border border-border/50 !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] p-4 shadow-xl space-y-2 min-w-[200px]">
                      <p className="text-sm font-medium text-foreground">{title}</p>
                      <div className="space-y-1.5">
                        {payload.map((p) => (
                          <div key={p.dataKey as string} className="flex items-center gap-2 text-sm">
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: p.color }}
                            />
                            <span className="text-muted-foreground">{p.name}</span>
                            <span className="font-mono ml-auto">{compactMoney(Math.round(p.value as number))}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                }}
              />

              <defs>
                {/* Lengthwise fade (dim base, full color at the tip) — same
                    treatment as the dashboard bar charts and donut slices. */}
                <linearGradient id="product-revenue-bar-fill" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="var(--chart-legend-revenue)" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="var(--chart-legend-revenue)" stopOpacity={0.9} />
                </linearGradient>
              </defs>
              {/* Daily revenue is a handful of discrete orders — bars read
                  honestly (zero days are gaps), unlike an interpolated area. */}
              <Bar
                dataKey="revenue"
                name={t('productStatistics.revenueOverTime.revenue')}
                fill="url(#product-revenue-bar-fill)"
                radius={[6, 6, 0, 0]}
                maxBarSize={maxBarSize}
                activeBar={{ fill: 'var(--chart-legend-revenue)', fillOpacity: 0.9 }}
                isAnimationActive={false}
              >
                <LabelList dataKey="revenue" content={renderBarLabel} />
              </Bar>
              {/* Outline-only so the revenue bar behind stays visible even
                  when ad spend exceeds revenue for the day. */}
              <Bar
                xAxisId="overlay"
                dataKey="adSpend"
                name={t('productStatistics.revenueOverTime.adSpend')}
                fill="transparent"
                stroke="var(--chart-legend-adspend)"
                strokeWidth={1.5}
                radius={[6, 6, 0, 0]}
                maxBarSize={maxBarSize}
                activeBar={{ fill: 'var(--chart-legend-adspend)', fillOpacity: 0.2 }}
                isAnimationActive={false}
              />

            </ComposedChart>
          </ChartContainer>
        </div>
      )}
    </div>
  )
}
